"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }var _react = require('react');
var _hooks = require('web/services/hooks');


var _actions = require('web/actions'); var _actions2 = _interopRequireDefault(_actions);
var _app = require('shared/selectors/app');
var _user = require('shared/selectors/user');
var _onboardingAnalytics = require('web/services/onboardingAnalytics');

/**
 * Lazy, session-cached SMS-trial eligibility for the declined-cohort in-app
 * banner. Eligibility is **server-authoritative**: `GetOrgOnboardingSurvey`
 * returns a `TrialEligible` flag computed by the same gate `GetCheckoutURL`
 * enforces (org-owned subscription + `sms`/`both` interest + no Stripe IDs).
 * The frontend cannot derive Stripe-ID state and must not try.
 *
 * The read is fetched once per org per session and cached in sessionStorage, so
 * it self-extinguishes once a trial starts (the flag flips false server-side).
 * A checkout success return invalidates the cache via
 * `invalidateSmsTrialEligibility`, forcing a fresh read so the banner disappears.
 */

const cacheKey = (orgKey) => `onb_trial_eligible_${orgKey}`

// Transient (per page load) in-flight guard so concurrent renders don't fire
// duplicate reads for the same org.
const inFlight = new Set()

// Failures are NOT cached (a transient blip must not hide the banner for the
// whole session) but they must not retry unbounded either: the settle
// re-render would otherwise refetch immediately, looping against a
// persistently failing backend. In-memory cooldown; a later render past the
// window (or a page load) retries.
const failedAt = new Map()
const FAILURE_RETRY_MS = 60000

// Positive entries expire: eligibility can flip false OUTSIDE this surface's
// invalidation hooks (a Settings-UI checkout, another tab), and a stale `true`
// keeps the banner routing into an onboarding checkout the backend now
// rejects. Negative entries stay for the session — a stale `false` only hides
// a prompt and self-heals via invalidateSmsTrialEligibility.
const POSITIVE_TTL_MS = 10 * 60000

const readCache = (orgKey) => {
  try {
    const v = sessionStorage.getItem(cacheKey(orgKey))
    if (v === '0') return false
    if (_optionalChain([v, 'optionalAccess', _ => _.startsWith, 'call', _2 => _2('1|')])) {
      const at = Number(v.slice(2))
      if (Number.isFinite(at) && Date.now() - at < POSITIVE_TTL_MS) return true
      return undefined
    }
    return undefined
  } catch (e) {
    return undefined
  }
}

const writeCache = (orgKey, eligible) => {
  try {
    sessionStorage.setItem(cacheKey(orgKey), eligible ? `1|${Date.now()}` : '0')
  } catch (e2) {
    // best-effort; storage may be unavailable in private modes
  }
}

/** Drops the cached eligibility so the next render re-reads it from the server. */
 const invalidateSmsTrialEligibility = (orgKey) => {
  try {
    sessionStorage.removeItem(cacheKey(orgKey))
  } catch (e3) {
    // best-effort
  }
  inFlight.delete(orgKey)
  failedAt.delete(orgKey)
}; exports.invalidateSmsTrialEligibility = invalidateSmsTrialEligibility

/**
 * Returns the first non-pending-admin org that is trial-eligible, or `null`.
 * Fetches lazily (socket-gated) and re-renders when a read settles.
 */
 const useSmsTrialEligibility = () => {
  const clientOpen = _hooks.useAppSelector.call(void 0, _app.selectIsClientOpen)
  const currentUser = _hooks.useAppSelector.call(void 0, _user.selectCurrentUser)
  const [, forceRender] = _react.useReducer.call(void 0, (x) => x + 1, 0)
  const mountedRef = _react.useRef.call(void 0, true)
  _react.useEffect.call(void 0, () => () => { mountedRef.current = false }, [])

  const adminOrgKeys = _react.useMemo.call(void 0, 
    () => _onboardingAnalytics.getNonPendingAdminOrgKeys.call(void 0, _optionalChain([currentUser, 'optionalAccess', _3 => _3.Relationships])),
    [currentUser],
  )

  // Intentionally dependency-less: the eligibility cache lives in sessionStorage
  // and can be invalidated by another surface (a checkout success return), so we
  // re-check each render and only fetch what is genuinely uncached / not in
  // flight. The guards make this idempotent — no fetch loop.
  _react.useEffect.call(void 0, () => {
    if (!clientOpen) return
    adminOrgKeys.forEach(orgKey => {
      if (readCache(orgKey) !== undefined || inFlight.has(orgKey)) return
      const lastFailure = failedAt.get(orgKey)
      if (lastFailure !== undefined && Date.now() - lastFailure < FAILURE_RETRY_MS) return
      inFlight.add(orgKey)
      _actions2.default.getOrgOnboardingSurvey({ OrgKey: orgKey })
        .then(r => {
          failedAt.delete(orgKey)
          writeCache(orgKey, _optionalChain([r, 'optionalAccess', _4 => _4.TrialEligible]) === true)
        })
        // A transient failure must stay retryable: caching it as `false` hid
        // the banner for the whole session for a genuinely eligible org. Only
        // real server answers are cached; the cooldown above keeps the retry
        // bounded.
        .catch(() => { failedAt.set(orgKey, Date.now()) })
        .finally(() => {
          inFlight.delete(orgKey)
          if (mountedRef.current) forceRender()
        })
    })
  })

  return adminOrgKeys.find(orgKey => readCache(orgKey) === true) || null
}; exports.useSmsTrialEligibility = useSmsTrialEligibility
